import React, {
  useEffect,
  useState,
} from "react";
import {
  Briefcase,
  ClipboardText,
  HardDrives,
  Wrench,
} from "@phosphor-icons/react";
import {
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";

import {
  mail as mailApi,
} from "@/api";
import MailAffairAssociationDialog from "@/components/mail/MailAffairAssociationDialog";
import MailDraftList from "@/components/mail/MailDraftList";
import MailScheduledList from "@/components/mail/MailScheduledList";
import MailInstallationAssociationDialog from "@/components/mail/MailInstallationAssociationDialog";
import MailInterventionAssociationDialog from "@/components/mail/MailInterventionAssociationDialog";
import MailMachineAssociationDialog from "@/components/mail/MailMachineAssociationDialog";
import { useAuth } from "@/context/AuthContext";
import {
  hasModuleAccess,
  hasPageAccess,
} from "@/lib/accessPermissions";
import Mail from "@/pages/Mail";


export default function MailWithAssociations() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const messageId = searchParams.get("message") || "";
  const folder = searchParams.get("folder") || "inbox";

  const canAssociateAffair = hasModuleAccess(
    user,
    "commercial"
  );
  const canAssociateMachine = hasModuleAccess(
    user,
    "gmao"
  );
  const canAssociateInstallation = hasPageAccess(
    user,
    "gmao",
    "installations"
  );
  const canAssociateIntervention = hasPageAccess(
    user,
    "gmao",
    "interventions"
  );

  const [message, setMessage] = useState(null);
  const [affairOpen, setAffairOpen] = useState(false);
  const [affairSaving, setAffairSaving] = useState(false);
  const [machineOpen, setMachineOpen] = useState(false);
  const [machineSaving, setMachineSaving] = useState(false);
  const [installationOpen, setInstallationOpen] = useState(false);
  const [installationSaving, setInstallationSaving] = useState(false);
  const [interventionOpen, setInterventionOpen] = useState(false);
  const [interventionSaving, setInterventionSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setAffairOpen(false);
    setMachineOpen(false);
    setInstallationOpen(false);
    setInterventionOpen(false);

    if (
      folder === "drafts"
      || folder === "scheduled"
      || !messageId
      || (
        !canAssociateAffair
        && !canAssociateMachine
        && !canAssociateInstallation
        && !canAssociateIntervention
      )
    ) {
      setMessage(null);
      return undefined;
    }

    mailApi
      .getMessage(messageId)
      .then((result) => {
        if (!cancelled) {
          setMessage(result || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMessage(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    folder,
    messageId,
    canAssociateAffair,
    canAssociateMachine,
    canAssociateInstallation,
    canAssociateIntervention,
  ]);

  async function associateAffair(affair) {
    if (!messageId || !affair?.id || affairSaving) return;

    setAffairSaving(true);
    try {
      const result = await mailApi.associateAffair(messageId, affair.id);
      setMessage((current) => ({
        ...(current || {}),
        prospectId: result.prospectId,
        opportunityId: result.opportunityId,
      }));
      setAffairOpen(false);
      toast.success("Message associé à l'affaire");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Association à l'affaire impossible"
      );
    } finally {
      setAffairSaving(false);
    }
  }

  async function associateMachine(machine) {
    if (!messageId || !machine?.id || machineSaving) return;

    setMachineSaving(true);
    try {
      const result = await mailApi.associateMachine(messageId, machine.id);
      setMessage((current) => ({
        ...(current || {}),
        prospectId: result.prospectId || current?.prospectId,
        machineId: result.machineId,
      }));
      setMachineOpen(false);
      toast.success("Message associé à la machine");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Association à la machine impossible"
      );
    } finally {
      setMachineSaving(false);
    }
  }

  async function associateInstallation(installation) {
    if (!messageId || !installation?.id || installationSaving) return;

    setInstallationSaving(true);
    try {
      const result = await mailApi.associateInstallation(
        messageId,
        installation.id
      );
      setMessage((current) => ({
        ...(current || {}),
        prospectId: result.prospectId || current?.prospectId,
        machineId: result.machineId || current?.machineId,
        installationId: result.installationId,
      }));
      setInstallationOpen(false);
      toast.success("Message associé à l'installation");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Association à l'installation impossible"
      );
    } finally {
      setInstallationSaving(false);
    }
  }

  async function associateIntervention(intervention) {
    if (!messageId || !intervention?.id || interventionSaving) return;

    setInterventionSaving(true);
    try {
      const result = await mailApi.associateIntervention(
        messageId,
        intervention.id
      );
      setMessage((current) => ({
        ...(current || {}),
        prospectId: result.prospectId || current?.prospectId,
        machineId: result.machineId || current?.machineId,
        installationId: result.installationId || current?.installationId,
        interventionId: result.interventionId,
      }));
      setInterventionOpen(false);
      toast.success("Message associé à l'intervention");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Association à l'intervention impossible"
      );
    } finally {
      setInterventionSaving(false);
    }
  }

  const showAffairAction = Boolean(
    canAssociateAffair
    && messageId
    && message
    && !message.opportunityId
  );

  const showMachineAction = Boolean(
    canAssociateMachine
    && messageId
    && message
    && !message.machineId
  );

  const showInstallationAction = Boolean(
    canAssociateInstallation
    && messageId
    && message
    && !message.installationId
  );

  const showInterventionAction = Boolean(
    canAssociateIntervention
    && messageId
    && message
    && !message.interventionId
  );

  const associationActions = [
    showAffairAction
      ? {
        icon: <Briefcase size={20} />,
        label: "Associer à une affaire",
        onClick: () => setAffairOpen(true),
        testId: "mail-associate-affair",
      }
      : null,
    showMachineAction
      ? {
        icon: <HardDrives size={20} />,
        label: "Associer à une machine",
        onClick: () => setMachineOpen(true),
        testId: "mail-associate-machine",
      }
      : null,
    showInstallationAction
      ? {
        icon: <Wrench size={20} />,
        label: "Associer à une installation",
        onClick: () => setInstallationOpen(true),
        testId: "mail-associate-installation",
      }
      : null,
    showInterventionAction
      ? {
        icon: <ClipboardText size={20} />,
        label: "Associer à une intervention",
        onClick: () => setInterventionOpen(true),
        testId: "mail-associate-intervention",
      }
      : null,
  ].filter(Boolean);

  return (
    <div
      className="acta-page-gutters relative min-h-0 min-w-0 lg:h-full lg:overflow-hidden"
      data-testid="mail-associations-page"
    >
      {folder === "drafts"
        ? <MailDraftList />
        : folder === "scheduled"
          ? <MailScheduledList />
          : <Mail messageActions={associationActions} />}

      {folder !== "drafts" && folder !== "scheduled" && (
        <>
          <MailAffairAssociationDialog
            open={affairOpen && showAffairAction}
            saving={affairSaving}
            onClose={() => setAffairOpen(false)}
            onSearchAffairs={mailApi.affairSuggestions}
            onAssociate={associateAffair}
          />

          <MailMachineAssociationDialog
            open={machineOpen && showMachineAction}
            saving={machineSaving}
            onClose={() => setMachineOpen(false)}
            onSearchMachines={mailApi.machineSuggestions}
            onAssociate={associateMachine}
          />

          <MailInstallationAssociationDialog
            open={installationOpen && showInstallationAction}
            saving={installationSaving}
            onClose={() => setInstallationOpen(false)}
            onSearchInstallations={mailApi.installationSuggestions}
            onAssociate={associateInstallation}
          />

          <MailInterventionAssociationDialog
            open={interventionOpen && showInterventionAction}
            saving={interventionSaving}
            onClose={() => setInterventionOpen(false)}
            onSearchInterventions={mailApi.interventionSuggestions}
            onAssociate={associateIntervention}
          />
        </>
      )}
    </div>
  );
}
